import type {
  DeskFeature,
  EditableFloorPlan,
  EditorFeature,
  ElementKind,
} from '../models/editor-feature';
import type { EditorLayerKey } from './layer-settings';

/**
 * Selection identity.
 *
 * A feature's own `id` is not enough on its own: the four editable kinds live in three
 * different layers, and nothing guarantees an id is unique across them. Keys are therefore
 * scoped by kind.
 */

export type SelectionKey = string;

export const selectionKeyOf = (feature: EditorFeature): SelectionKey => `${feature.kind}:${feature.id}`;

export const kindOfSelectionKey = (key: SelectionKey): ElementKind => key.split(':')[0] as ElementKind;

/**
 * Everything the admin can select, in the order it is painted.
 *
 * The drawn areas are in here as of the advanced tool: the border, the room zones and the
 * floor's walls used to be backdrop that only the renderer ever saw.
 *
 * The order is painting order — the same one `buildDrawPlan` uses — because `hit-test` reads
 * it to decide what a click landed on, and "what is on top" is a question about what is drawn.
 * It used to list the border first and the floor third, which matched nothing: the floor was
 * therefore tested *before* the rooms drawn over it, and a stored plan's full-storey floor
 * plate swallowed every click meant for a room.
 */
export const editableFeaturesOf = (floorPlan: EditableFloorPlan): EditorFeature[] => [
  ...floorPlan.floorBackdrop,
  ...floorPlan.rooms,
  ...floorPlan.border,
  ...floorPlan.deskShapes,
  ...floorPlan.desks,
  ...floorPlan.labels,
];

/**
 * Everything drawn on one layer.
 *
 * What makes a layer row in the panel an editing tool rather than a light switch: "select the
 * whole workplaces layer" is the first step of half the bulk edits this editor exists for,
 * and doing it by rubber-banding the floor catches the rooms and the furniture underneath.
 *
 * Retired desks are the one split. They live in the same collection as every other desk and
 * are told apart by a date, so the desks layer means the desks still in service and the
 * retired row means the rest — which is what each of those rows draws.
 */
export const featuresOnLayer = (
  floorPlan: EditableFloorPlan,
  layer: EditorLayerKey,
  retiredDeskIds?: ReadonlySet<string>,
): EditorFeature[] => {
  const isRetired = (desk: DeskFeature): boolean => retiredDeskIds?.has(desk.featureId) ?? false;

  switch (layer) {
    case 'border':
      return [...floorPlan.border];
    case 'rooms':
      return [...floorPlan.rooms];
    case 'floor':
      return [...floorPlan.floorBackdrop];
    case 'deskShapes':
      return [...floorPlan.deskShapes];
    case 'desks':
      return floorPlan.desks.filter((desk) => !isRetired(desk));
    case 'retiredDesks':
      return floorPlan.desks.filter(isRetired);
    default:
      return [...floorPlan.labels];
  }
};

export const featuresForKeys = (
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
): EditorFeature[] => editableFeaturesOf(floorPlan).filter((feature) => keys.has(selectionKeyOf(feature)));
